import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicBrowseShell from '../../components/public/PublicBrowseShell';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Input from '../../components/ui/Input';
import { teamService } from '../../services/teamService';

const statusBadgeClass = (status) => {
  if (status === 'active') return 'bg-success/10 text-success';
  if (status === 'pending_approval' || status === 'pending') return 'bg-warning/10 text-warning';
  return 'bg-muted text-muted-foreground';
};

const ClubsListPage = () => {
  const [clubs, setClubs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    document.title = 'Clubs - Tanzania Basketball Federation';
    loadClubs();
  }, []);

  const loadClubs = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await teamService.getAll();
      setClubs(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load clubs');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredClubs = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return clubs;
    return clubs.filter((club) =>
      [club?.name, club?.shortName, club?.city, club?.region]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [clubs, search]);

  return (
    <PublicBrowseShell title="Clubs" subtitle="Registered basketball clubs and teams">
      <div className="mb-6 max-w-md">
        <Input
          type="search"
          placeholder="Search clubs..."
          iconName="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="Loader2" size={40} className="text-primary mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Loading clubs...</p>
        </div>
      ) : error ? (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} />
            <p className="font-medium">{error}</p>
          </div>
        </div>
      ) : filteredClubs?.length === 0 ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="Shield" size={40} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Clubs Found</h3>
          <p className="text-muted-foreground">Try adjusting your search.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClubs.map((club) => (
            <Link
              key={club.id}
              to={`/clubs/${club.id}`}
              className="bg-card rounded-lg border border-border card-shadow p-6 hover:shadow-lg transition-all hover:scale-[1.02]"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-lg bg-green-700/10 flex items-center justify-center overflow-hidden shrink-0">
                  {club?.logoUrl ? (
                    <Image src={club.logoUrl} alt={club.name} className="w-full h-full object-cover" />
                  ) : (
                    <Icon name="Shield" size={26} className="text-green-700" />
                  )}
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-foreground truncate">{club?.name || 'Unnamed Club'}</h3>
                  <p className="text-sm text-muted-foreground truncate">
                    {[club?.city, club?.region].filter(Boolean).join(', ') || 'Location N/A'}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{club?.category || 'Basketball'}</span>
                <span className={`text-xs px-2 py-1 rounded-full capitalize ${statusBadgeClass(club?.teamStatus)}`}>
                  {(club?.teamStatus || 'pending').replace(/_/g, ' ')}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </PublicBrowseShell>
  );
};

export default ClubsListPage;
